import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import classNames from 'classnames';

import { navigate } from 'gatsby';
import Image from '../Image';
import { formatPrice } from '../../utils';
import { getProductsByHandle } from '../../utils/clients/shopify/shopify';

const ns = `cart-product-slider`;
const visibleSlidesCount = 2;

const ArrowIcon = () => {
	return (
		<svg
			width="100%"
			height="100%"
			viewBox="0 0 19 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M4.09047 1.20515C3.36002 0.525875 2.22903 0.525876 1.49858 1.20515C0.689124 1.95789 0.689126 3.23957 1.49858 3.99231L11.2611 13.0708C12.9625 14.653 12.9625 17.347 11.2611 18.9292L1.49858 28.0077C0.689124 28.7604 0.689123 30.0421 1.49858 30.7949C2.22903 31.4741 3.36002 31.4741 4.09047 30.7949L16.8501 18.9292C18.5515 17.347 18.5515 14.653 16.8501 13.0708L4.09047 1.20515Z"
				fill="currentColor"
			/>
		</svg>
	);
};

const CartProductSlider = ({onFlyoutCartClose, location}) => {
	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	const sliderRef = useRef(null);
	const [products, setProducts] = useState([]);
	const [currentSlide, setCurrentSlide] = useState(0);

	useEffect(() => {
		// Todo: Add logic to fetch products based on practitioner account type
		const handles = [
			'ultimate-omega-2x-sport',
			'algae-omega',
			'multi-minerals-without-iron-copper',
			'arctic-cod-liver-oil-pro',
		];
		getProductsByHandle(handles)
			.then(allProducts => {
				setProducts(allProducts);
			});
	}, []);

	if (!products.length) {
		return null;
	}

	const slidesToShow = Math.min(visibleSlidesCount, products.length);
	const maxSlideStart = Math.max(products.length - slidesToShow, 0);
	const activeSlideIndexes = Array.from(
		{ length: slidesToShow },
		(_, offset) => currentSlide + offset
	);
	const isPreviousDisabled = currentSlide === 0;
	const isNextDisabled = currentSlide >= maxSlideStart;

	const handlePreviousRecommendations = () => {
		sliderRef.current?.slickPrev();
	};

	const handleNextRecommendations = () => {
		sliderRef.current?.slickNext();
	};

	return (
		<div className={rootClassnames}>
			<div className={`${ ns }__nav`}>
				<div className={`${ ns }__nav-title`}>
					<p>Customers also bought:</p>
				</div>
				<div className="slick-slider">
					<button
						type="button"
						className="slick-prev"
						onClick={handlePreviousRecommendations}
						disabled={isPreviousDisabled}
						aria-label="Previous product recommendations"
					>
						<ArrowIcon />
					</button>
					<button
						type="button"
						className="slick-next"
						onClick={handleNextRecommendations}
						disabled={isNextDisabled}
						aria-label="Next product recommendations"
					>
						<ArrowIcon />
					</button>
				</div>
			</div>
			<div className={`${ ns }__slider`}>
				<Slider
					ref={sliderRef}
					arrows={false}
					afterChange={setCurrentSlide}
					infinite={false}
					slidesToShow={slidesToShow}
					slidesToScroll={slidesToShow}
				>
					{products.map((product, index) => {
						const flatProduct = {
							...product,
							...product.images?.[0],
							...product.priceRange?.minVariantPrice,
						};
						const productUrl = `/products/nordic-naturals/${ product.handle }`;
						const isSlideActive = activeSlideIndexes.includes(index);

						return (
							<div key={product.handle}>
								<button
									type="button"
									onClick={() => {
										onFlyoutCartClose();
										navigate(productUrl, {
											state: {
												prevPath: location?.pathname?.includes('search') ? 'search results' : location?.pathname || '',
											}
										});
									}}
									aria-label={`View recommended product ${ flatProduct.title }`}
									className={`${ ns }__slide`}
									tabIndex={isSlideActive ? 0 : -1}
								>
									<div className={`${ ns }__slide-image`}>
										<Image
											sourceUrl={flatProduct.src || flatProduct.originalSrc}
											altText={flatProduct.altText || `${ flatProduct.title } product image`}
										/>
									</div>
									<div className={`${ ns }__slide-content`}>
										<p className={`${ ns }__slide-title`}>
											{flatProduct.title}
										</p>
										<p className={`${ ns }__slide-price`}>
											From
											<br />
											{formatPrice(
												flatProduct.currencyCode,
												Number(flatProduct.amount)
											)}
										</p>
									</div>
								</button>
							</div>
						);
					})}
				</Slider>
			</div>
		</div>
	);
};

export default CartProductSlider;
